/**
 * Completion content for the "select-services" activity.
 * Two-step SteppedForm wizard: select HealthcareService resources for each
 * ServiceRequest need, then confirm and submit.
 * @agentic connections
 */

import {
  Stack,
  Text,
  Alert,
  Skeleton,
  Paper,
  Group,
  Badge,
  Select,
  Textarea,
  TextInput,
  ActionIcon,
  Button,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconArrowBack,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { getDisplayString } from "@medplum/core";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Practitioner,
  ServiceRequest,
  HealthcareService,
} from "@medplum/fhirtypes";
import {
  HEALTHCARE_SERVICE_CODESYSTEMS,
  SERVICE_MODE_SELECT_OPTIONS,
} from "@benchmark/core/constants";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { useCarePlanServiceRequests } from "@/hooks/domain/useCarePlanServiceRequests";
import { useHealthcareServiceSearch } from "@/hooks/shared/useFHIROptions";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import type {
  OfferServicesFormData,
  ResourceSelection,
} from "@/types/domain/offer-services";
import { saveTaskDraft } from "@benchmark/core/actions";
import type { FormStep as FormStepType } from "@benchmark/ui/components/atomic";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { PriorReviewsSection } from "./PriorReviewsSection";
import {
  getCarePlanIdFromTask,
  getConditionDisplays,
  getDecisionNotes,
  getLatestApprovalNote,
  getLatestSelectionNote,
  getPreferencesText,
  getServiceRequestVisitModeDisplay,
  getServiceTypeDisplay,
  getTaskDraftFormState,
} from "@benchmark/core/resolvers";

/** Maximum number of service/provider URLs per ServiceRequest selection. */
const MAX_WEBSITE_URLS = 3;

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

/**
 * Narrows a saved Task draft to the form shape, dropping anything malformed.
 * @agentic connections
 */
const parseSelectServicesDraft = (
  draft: Record<string, unknown> | undefined,
): OfferServicesFormData | undefined => {
  if (!draft) return undefined;
  const rawSelections =
    draft.selections && typeof draft.selections === "object"
      ? (draft.selections as Record<string, unknown>)
      : {};
  const selections: Record<string, ResourceSelection> = {};
  for (const [id, value] of Object.entries(rawSelections)) {
    if (!value || typeof value !== "object") continue;
    const s = value as Record<string, unknown>;
    const urls = Array.isArray(s.websiteUrls)
      ? s.websiteUrls
          .filter((u): u is string => typeof u === "string")
          .slice(0, MAX_WEBSITE_URLS)
      : [];
    selections[id] = {
      healthcareServiceId: asString(s.healthcareServiceId),
      healthcareServiceDisplay: asString(s.healthcareServiceDisplay),
      mode: asString(s.mode),
      rationale: asString(s.rationale),
      websiteUrls: urls.length > 0 ? urls : [""],
    };
  }
  return { selections, notes: asString(draft.notes) };
};

/**
 * True when anything has been entered anywhere in the form.
 * @agentic connections
 */
const hasFormContent = (values: OfferServicesFormData): boolean =>
  !!values.notes.trim() ||
  Object.values(values.selections).some(
    (s) =>
      !!s.healthcareServiceId.trim() ||
      !!s.mode.trim() ||
      !!s.rationale.trim() ||
      s.websiteUrls.some((url) => !!url.trim()),
  );

/**
 * Extracts the first existing mode code from a ServiceRequest's locationCode.
 * @agentic connections
 */
const getExistingMode = (sr: ServiceRequest): string =>
  (sr.locationCode?.[0]?.coding?.[0]?.code as string) ?? "";

/**
 * Extracts existing HealthcareService performer from a ServiceRequest (set in prior rounds).
 * @agentic connections
 */
const getExistingPerformer = (
  sr: ServiceRequest,
): { id: string; display: string } | null => {
  const ref = sr.performer?.find((p) =>
    p.reference?.startsWith("HealthcareService/"),
  );
  if (!ref?.reference) return null;
  const id = ref.reference.replace("HealthcareService/", "");
  return { id, display: ref.display || id };
};

/**
 * Props for ServiceRequestResourceCard.
 * @agentic connections
 */
type ServiceRequestResourceCardProps = {
  sr: ServiceRequest;
  index: number;
  totalCount: number;
  selectedServiceId: string;
  selectedServiceDisplay: string;
  selectedMode: string;
  rationale: string;
  websiteUrls: string[];
  onServiceChange: (serviceId: string, display: string) => void;
  onModeChange: (mode: string) => void;
  onRationaleChange: (rationale: string) => void;
  onWebsiteUrlsChange: (urls: string[]) => void;
};

/**
 * Card for selecting a HealthcareService for a single ServiceRequest.
 * @agentic connections
 */
const ServiceRequestResourceCard = ({
  sr,
  index,
  totalCount,
  selectedServiceId,
  selectedServiceDisplay,
  selectedMode,
  rationale,
  websiteUrls,
  onServiceChange,
  onModeChange,
  onRationaleChange,
  onWebsiteUrlsChange,
}: ServiceRequestResourceCardProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { resources, isSearching } = useHealthcareServiceSearch(searchQuery);

  const serviceTypeDisplay = getServiceTypeDisplay(sr);
  const conditions = getConditionDisplays(sr);
  const existingModeDisplay = getServiceRequestVisitModeDisplay(sr);
  const preferences = getPreferencesText(sr);
  const decisionHistory = getDecisionNotes(sr);

  const hasMinimumChars = searchQuery.trim().length >= 2;

  const selectOptions = useMemo(() => {
    const optionsFromSearch: Array<{ value: string; label: string }> = resources
      .filter((hs: HealthcareService) => hs.id && hs.name?.trim())
      .map((hs: HealthcareService) => ({
        value: hs.id as string,
        label: hs.name as string,
      }));

    if (
      selectedServiceId &&
      !optionsFromSearch.some((o) => o.value === selectedServiceId)
    ) {
      optionsFromSearch.unshift({
        value: selectedServiceId,
        label: selectedServiceDisplay || selectedServiceId,
      });
    }

    return optionsFromSearch;
  }, [resources, selectedServiceId, selectedServiceDisplay]);

  const handleServiceSelect = useCallback(
    (value: string | null) => {
      if (!value) {
        onServiceChange("", "");
        return;
      }
      const matched = resources.find(
        (hs: HealthcareService) => hs.id === value,
      );
      onServiceChange(value, matched?.name || value);
    },
    [resources, onServiceChange],
  );

  const badgeLabel =
    totalCount > 1
      ? `${index + 1} \u00B7 ${serviceTypeDisplay.toUpperCase()}`
      : serviceTypeDisplay.toUpperCase();

  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Badge variant="filled" color="purple" size="lg" radius="sm">
            {badgeLabel}
          </Badge>
        </Group>

        <Group gap={4} wrap="wrap">
          {conditions.map((c) => (
            <Badge key={c} size="xs" variant="light" color="purple">
              {c}
            </Badge>
          ))}
          {existingModeDisplay !== "—" && (
            <Badge size="xs" variant="light" color="gray">
              {existingModeDisplay}
            </Badge>
          )}
        </Group>

        {preferences && (
          <Text size="sm" c="purple.7" fw={500}>
            Member Preferences: {preferences}
          </Text>
        )}

        <PriorReviewsSection decisionHistory={decisionHistory} />

        <Group grow align="flex-start">
          <Select
            label="Service"
            placeholder="Search healthcare services..."
            data={selectOptions}
            value={selectedServiceId || null}
            onChange={handleServiceSelect}
            searchable
            clearable
            withAsterisk
            onSearchChange={setSearchQuery}
            filter={({ options }) => options}
            nothingFoundMessage={
              !hasMinimumChars
                ? "Type at least 2 characters to search"
                : isSearching
                  ? "Searching..."
                  : resources.length === 0
                    ? "No healthcare services found"
                    : "Type at least 2 characters to search"
            }
          />

          <Select
            label="Service Mode"
            placeholder="Select delivery mode..."
            data={SERVICE_MODE_SELECT_OPTIONS}
            value={selectedMode || null}
            onChange={(v) => onModeChange(v ?? "")}
            clearable
          />
        </Group>

        <Stack gap="xs">
          {websiteUrls.map((url, urlIndex) => (
            <Group key={urlIndex} gap="xs" align="flex-end">
              <TextInput
                label={urlIndex === 0 ? "Service/Provider Website" : undefined}
                placeholder="https://..."
                value={url}
                onChange={(e) => {
                  const updated = [...websiteUrls];
                  updated[urlIndex] = e.currentTarget.value;
                  onWebsiteUrlsChange(updated);
                }}
                style={{ flex: 1 }}
              />
              {websiteUrls.length > 1 && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Remove URL"
                  onClick={() => {
                    onWebsiteUrlsChange(
                      websiteUrls.filter((_, i) => i !== urlIndex),
                    );
                  }}
                >
                  <IconX size={16} />
                </ActionIcon>
              )}
            </Group>
          ))}
          {websiteUrls.length < MAX_WEBSITE_URLS && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => onWebsiteUrlsChange([...websiteUrls, ""])}
              fullWidth
            >
              Add Another URL
            </Button>
          )}
        </Stack>

        <Textarea
          label="Service Selection Rationale"
          placeholder="Why is this service a good fit for this member's need?"
          value={rationale}
          onChange={(e) => onRationaleChange(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={4}
        />
      </Stack>
    </Paper>
  );
};

/**
 * Two-step completion wizard for selecting referral services.
 * @agentic connections
 */
export const SelectServicesCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDone,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const carePlanId = getCarePlanIdFromTask(task);

  const {
    serviceRequests,
    isLoading,
    error: srError,
  } = useCarePlanServiceRequests(carePlanId);

  const [submitting, setSubmitting] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [draft] = useState(() =>
    parseSelectServicesDraft(getTaskDraftFormState(task)),
  );
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const rejectionContext = useMemo(() => {
    const raw = task.statusReason?.text?.trim();
    if (!raw) return null;

    let label: string;
    let rationale: string;
    if (raw.startsWith("Denied:")) {
      label = "Services Denied";
      rationale = raw.slice("Denied:".length).trim();
    } else if (raw.startsWith("Needs more information:")) {
      label = "More Information Requested";
      rationale = raw.slice("Needs more information:".length).trim();
    } else if (raw.startsWith("Member declined:")) {
      label = "Member Declined";
      rationale = raw.slice("Member declined:".length).trim();
    } else {
      return null;
    }

    const rejectionCycles = Array.from(
      new Map(
        serviceRequests.flatMap(getDecisionNotes).map((r) => [r.time ?? "", r]),
      ).values(),
    );
    const latestRejection =
      rejectionCycles.length > 0
        ? rejectionCycles[rejectionCycles.length - 1]
        : undefined;

    return {
      label,
      rationale,
      reviewerName: latestRejection?.authorString,
      reviewerTime: latestRejection?.time,
      rejectionCount: rejectionCycles.length,
    };
  }, [task.statusReason?.text, serviceRequests]);

  const form = useForm<OfferServicesFormData>({
    initialValues: {
      selections: {},
      notes: draft?.notes ?? "",
    },
  });

  useEffect(() => {
    if (serviceRequests.length === 0) return;

    const existingSelections = form.values.selections;
    const needsInit = serviceRequests.some(
      (sr) => sr.id && !(sr.id in existingSelections),
    );

    if (needsInit) {
      const newSelections: Record<string, ResourceSelection> = {};
      for (const sr of serviceRequests) {
        if (!sr.id) continue;
        const existing = getExistingPerformer(sr);
        const priorNote = getLatestSelectionNote(sr);
        newSelections[sr.id] = existingSelections[sr.id] ||
          draft?.selections[sr.id] || {
            healthcareServiceId: existing?.id ?? "",
            healthcareServiceDisplay: existing?.display ?? "",
            mode: getExistingMode(sr),
            rationale: priorNote?.rationale ?? "",
            websiteUrls:
              priorNote && priorNote.websiteUrls.length > 0
                ? priorNote.websiteUrls
                : [""],
          };
      }
      form.setFieldValue("selections", newSelections);
    }
  }, [serviceRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAnySelection = useMemo(
    () =>
      Object.values(form.values.selections).some((s) => s.healthcareServiceId),
    [form.values.selections],
  );

  useEffect(() => {
    const isDirty =
      hasAnySelection ||
      !!form.values.notes ||
      !!nextTaskOwnerOverride ||
      !!careMinutes;
    onDirtyChange?.(isDirty);
  }, [
    hasAnySelection,
    form.values.notes,
    nextTaskOwnerOverride,
    careMinutes,
    onDirtyChange,
  ]);

  const serviceRequestIds = useMemo(
    () => serviceRequests.map((sr) => sr.id).filter(Boolean) as string[],
    [serviceRequests],
  );

  const steps: FormStepType<OfferServicesFormData>[] = useMemo(
    () => [
      {
        id: "services",
        title: "Service(s)",
        subtitle: "Select a service for each identified need",
        validate: (values: OfferServicesFormData) => {
          const missing = serviceRequestIds.filter(
            (id) => !values.selections[id]?.healthcareServiceId,
          );
          if (missing.length > 0) {
            return {
              selections:
                missing.length === serviceRequestIds.length
                  ? "Select a service for each identified need"
                  : `${missing.length} service need(s) still require a selection`,
            };
          }
          return null;
        },
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [serviceRequestIds],
  );

  const handleSubmit = useCallback(
    async (values: OfferServicesFormData) => {
      setSubmitting(true);
      setSubmitError(null);

      try {
        const updatePromises: Promise<ServiceRequest>[] = [];

        for (const sr of serviceRequests) {
          if (!sr.id) continue;
          const selection = values.selections[sr.id];
          if (!selection?.healthcareServiceId) continue;

          const modeOption = SERVICE_MODE_SELECT_OPTIONS.find(
            (o) => o.value === selection.mode,
          );

          const authorString = profile ? getDisplayString(profile) : undefined;
          const now = new Date().toISOString();

          const existingPerformer = getExistingPerformer(sr);
          const priorApproval = getLatestApprovalNote(sr);
          const priorSelection = getLatestSelectionNote(sr);
          const rationale = selection.rationale.trim();
          const nonEmptyUrls = selection.websiteUrls
            .map((u) => u.trim())
            .filter(Boolean);
          const selectionUnchanged =
            existingPerformer?.id === selection.healthcareServiceId &&
            priorApproval &&
            priorSelection?.serviceName ===
              selection.healthcareServiceDisplay &&
            (priorSelection.rationale ?? "") === rationale &&
            priorSelection.websiteUrls.length === nonEmptyUrls.length &&
            priorSelection.websiteUrls.every(
              (url, index) => url === nonEmptyUrls[index],
            );

          const existingNotes = sr.note ?? [];
          const noteLines = [
            `ServiceName: ${selection.healthcareServiceDisplay}`,
            `ServiceRef: HealthcareService/${selection.healthcareServiceId}`,
            rationale && rationale,
            nonEmptyUrls.length > 0 && `Website: ${nonEmptyUrls.join(" ; ")}`,
          ].filter(Boolean);

          const selectionNote = selectionUnchanged
            ? []
            : [
                {
                  ...(authorString && { authorString }),
                  time: now,
                  text: `Service Selection: ${noteLines.join(" | ")}`,
                },
              ];

          const updatedSr: ServiceRequest = {
            ...sr,
            performer: [
              {
                reference: `HealthcareService/${selection.healthcareServiceId}`,
                display: selection.healthcareServiceDisplay,
              },
            ],
            ...(selection.mode &&
              modeOption && {
                locationCode: [
                  {
                    coding: [
                      {
                        system:
                          HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                        code: selection.mode,
                        display: modeOption.label,
                      },
                    ],
                  },
                ],
              }),
            note: [...existingNotes, ...selectionNote],
          };

          updatePromises.push(medplum.updateResource(updatedSr));
        }

        await Promise.all(updatePromises);

        const selectionsById = new Map(Object.entries(values.selections));
        const selectionSummaries = serviceRequests.flatMap((sr) => {
          const srId = sr.id;
          const selection = srId ? selectionsById.get(srId) : undefined;
          if (!srId || !selection?.healthcareServiceId) {
            return [];
          }

          return [
            `${getServiceTypeDisplay(sr)}: ${selection.healthcareServiceDisplay}`,
          ];
        });

        const statusReason = [
          selectionSummaries.length > 0 &&
            `Selected services: ${selectionSummaries.join("; ")}`,
          values.notes && `Notes: ${values.notes}`,
        ]
          .filter(Boolean)
          .join(". ");

        await onComplete(statusReason || undefined);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
          notes: values.notes || undefined,
        });
      } catch (err) {
        setSubmitError(
          err instanceof Error
            ? err.message
            : "Failed to update service requests",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      medplum,
      profile,
      serviceRequests,
      onComplete,
      teeForTask,
      task,
      careMinutes,
    ],
  );

  const hasContent = hasFormContent(form.values);
  const canSaveDraft = hasContent || !!draft;

  const handleSaveDraft = useCallback(async () => {
    if (!task.id || !patientId) return;
    setDraftSaving(true);
    setSubmitError(null);
    try {
      await saveTaskDraft(medplum, {
        taskId: task.id,
        patientId,
        formState: hasContent
          ? { selections: form.values.selections, notes: form.values.notes }
          : null,
      });
      onDone();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save draft",
      );
    } finally {
      setDraftSaving(false);
    }
  }, [medplum, task.id, patientId, hasContent, form.values, onDone]);

  if (!patientId || !carePlanId) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Unable to determine patient or care plan from this task.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={20} />
        <Skeleton height={120} />
        <Skeleton height={120} />
        <Skeleton height={40} />
      </Stack>
    );
  }

  if (srError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Failed to load service requests: {srError}
      </Alert>
    );
  }

  if (serviceRequests.length === 0) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        color="orange"
        variant="light"
      >
        No service needs found for this care plan. Intake transition may not
        have created service requests yet.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {rejectionContext && (
        <Alert
          icon={<IconArrowBack size={16} />}
          color="red"
          variant="light"
          title={rejectionContext.label}
        >
          <Stack gap={4}>
            <Text size="sm">
              This task was returned. Review the per-service feedback below and
              resubmit your selections.
            </Text>
            {rejectionContext.reviewerName && (
              <Text size="xs" c="dimmed">
                Reviewed by {rejectionContext.reviewerName}
                {rejectionContext.reviewerTime &&
                  ` on ${new Date(rejectionContext.reviewerTime).toLocaleDateString()}`}
              </Text>
            )}
            {rejectionContext.rejectionCount > 1 && (
              <Badge size="xs" variant="light" color="red">
                Selection attempt {rejectionContext.rejectionCount + 1}
              </Badge>
            )}
          </Stack>
        </Alert>
      )}

      {submitError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {submitError}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Save Selected Services"
        onCancel={onCancel}
        onSaveDraft={canSaveDraft ? handleSaveDraft : undefined}
        isDraftSaving={draftSaving}
      >
        <FormStep id="services">
          <Stack gap="md">
            <Stack gap={4}>
              <Text size="sm" fw={600} tt="uppercase" c="dimmed">
                Select Service(s) for Referral
              </Text>
              <Text size="sm" c="dimmed">
                Select the best-fit referral service for each identified service
                need.
              </Text>
            </Stack>

            {serviceRequests.map((sr, index) => {
              if (!sr.id) return null;
              const selection = form.values.selections[sr.id];
              if (!selection) return null;

              const srId = sr.id;
              return (
                <ServiceRequestResourceCard
                  key={srId}
                  sr={sr}
                  index={index}
                  totalCount={serviceRequests.length}
                  selectedServiceId={selection.healthcareServiceId}
                  selectedServiceDisplay={selection.healthcareServiceDisplay}
                  selectedMode={selection.mode}
                  rationale={selection.rationale}
                  websiteUrls={selection.websiteUrls}
                  onServiceChange={(serviceId, display) => {
                    form.setFieldValue(
                      `selections.${srId}.healthcareServiceId`,
                      serviceId,
                    );
                    form.setFieldValue(
                      `selections.${srId}.healthcareServiceDisplay`,
                      display,
                    );
                  }}
                  onModeChange={(mode) => {
                    form.setFieldValue(`selections.${srId}.mode`, mode);
                  }}
                  onRationaleChange={(rationaleValue) => {
                    form.setFieldValue(
                      `selections.${srId}.rationale`,
                      rationaleValue,
                    );
                  }}
                  onWebsiteUrlsChange={(urls) => {
                    form.setFieldValue(`selections.${srId}.websiteUrls`, urls);
                  }}
                />
              );
            })}

            <Textarea
              label="Notes"
              description="Internal notes for approval/offer handoff"
              placeholder="Add rationale for selected services..."
              value={form.values.notes}
              onChange={(e) =>
                form.setFieldValue("notes", e.currentTarget.value)
              }
              autosize
              minRows={2}
              maxRows={6}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
